import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, makeStyles } from '@fluentui/react-components';
import { AlertRegular } from '@fluentui/react-icons';
import { IDocumentDetails } from '../../../@types/models';
import { getEnvironment360URL } from '../../../services/configs/appConfig';
import { navigateSafely } from '../../../utils/navigation';

interface ImportArchiveLocationState {
    insertMessage?: string;
    importArchiveLink?: string;
    documentDetails?: IDocumentDetails;
    recordNumber?: number;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '6px',
        border: '1px solid #e0e0e0',
        maxWidth: '480px',
        margin: '10px auto',
        gap: '20px'
    },
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        backgroundColor: '#e91e63',
        marginBottom: '8px'
    },
    icon: {
        fontSize: '32px',
        color: '#ffffff'
    },
    title: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#424242',
        textAlign: 'center',
        margin: '0'
    },
    message: {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#666666',
        textAlign: 'center',
        margin: '0',
        maxWidth: '400px'
    },
    linkContainer: {
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    linkText: {
        fontSize: '14px',
        color: '#666666'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '8px'
    },
    button: {
        paddingLeft: '32px',
        paddingRight: '32px'
    }
});

export const ImportArchivePage: React.FC = () => {
    const styles = useStyles();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const state = location.state as ImportArchiveLocationState;
    const defaultMessage = t('Save failed due to an issue with the archiving process.');
    const insertMessage = state?.insertMessage || defaultMessage;

    // Construct the new import archive link format
    const getImportArchiveUrl = () => {
        const recordNumber = state?.recordNumber || state?.documentDetails?.DocumentRecno;
        if (recordNumber) {
            return `/locator/DMS/Dialog/ConnectedDocumentDialog?recno=${recordNumber}&SAVEAS=1&MoveTo=1&dialogCloseMode=WINDOWNOCLOSE&dialogFromAddin=true`;
        }
        return null;
    };

    const handleOpenWebClient = () => {
        const importArchiveUrl = getImportArchiveUrl();
        if (importArchiveUrl) {
            // Get base URL using the centralized method
            const baseUrl = getEnvironment360URL() || globalThis.location.origin;
            const fullUrl = baseUrl + importArchiveUrl;
            globalThis.open(fullUrl, '_blank');
        }
        // If no record number is available, we'll silently fail
    };

    const handleGoBackToRegistration = () => {
        // Navigate back to home page
        navigateSafely(navigate, '/archivehome');
    };

    return (
        <div className={styles.container}>
            <div className={styles.iconContainer}>
                <AlertRegular className={styles.icon} />
            </div>

            <h1 className={styles.title}>
                {t('Saving failed')}
            </h1>

            <p className={styles.message}>
                {insertMessage}
            </p>

            <div className={styles.linkContainer}>
                <div className={styles.linkText}>
                    {t('You can edit the partially saved file in the 360° web client')}
                </div>
                <Button
                    appearance="primary"
                    className={styles.button}
                    onClick={handleOpenWebClient}
                >
                    {t('Open web client')}
                </Button>
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    appearance="secondary"
                    className={styles.button}
                    onClick={handleGoBackToRegistration}
                >
                    {t('Go back to registration')}
                </Button>
            </div>
        </div>
    );
};

export default ImportArchivePage;